import { Platform } from 'react-native'

// ─── Font Family ───────────────────────────────────────────────
// Inter loaded via expo-font in root layout. Fallback to system font.
export const fontFamily = {
  regular: 'Inter_400Regular',
  medium: 'Inter_500Medium',
  semiBold: 'Inter_600SemiBold',
  bold: 'Inter_700Bold',
  // Fallback for before fonts load
  system: Platform.select({ ios: 'System', android: 'Roboto', default: 'sans-serif' }),
}

// ─── Colors ────────────────────────────────────────────────────
export const colors = {
  // Brand
  primary: '#2563eb',
  primaryDark: '#1d4ed8',
  primaryLight: '#dbeafe',
  primarySubtle: '#eff6ff',
  secondary: '#6366f1',

  // Semantic
  success: '#059669',
  successDark: '#047857',
  successLight: '#ecfdf5',
  successMuted: '#d1fae5',
  ctaGreen: '#16a34a',
  ctaGreenDark: '#15803d',
  warning: '#d97706',
  warningDark: '#b45309',
  warningLight: '#fffbeb',
  warningMuted: '#fef3c7',
  error: '#dc2626',
  errorDark: '#b91c1c',
  errorLight: '#fef2f2',
  errorMuted: '#fee2e2',

  // Surfaces
  background: '#ffffff',
  surface: '#f8fafc',
  surfaceHover: '#f1f5f9',
  surfaceElevated: '#ffffff',

  // Borders
  border: '#e2e8f0',
  borderLight: '#f1f5f9',
  borderFocus: '#2563eb',

  // Text
  text: {
    primary: '#0f172a',
    secondary: '#475569',
    muted: '#64748b',
    inverse: '#ffffff',
  },

  // Categories
  categories: {
    energy: '#ca8a04',
    mobile: '#2563eb',
    internet: '#9333ea',
  },
  categoriesBg: {
    energy: '#fef9c3',
    mobile: '#dbeafe',
    internet: '#f3e8ff',
  },

  // Provider avatar palette
  providerColors: [
    { bg: '#dbeafe', text: '#1d4ed8' },
    { bg: '#d1fae5', text: '#047857' },
    { bg: '#f3e8ff', text: '#7c3aed' },
    { bg: '#fef3c7', text: '#b45309' },
    { bg: '#ffe4e6', text: '#be123c' },
    { bg: '#cffafe', text: '#0e7490' },
  ],
}

// ─── Spacing (8px base grid) ───────────────────────────────────
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  '2xs': 2,
  '3xl': 64,
}

// ─── Border Radius ─────────────────────────────────────────────
export const borderRadius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  full: 9999,
}

// ─── Shadows ───────────────────────────────────────────────────
export const shadows = {
  sm: Platform.select({
    ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2 },
    android: { elevation: 1 },
    default: { boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  }) as any,
  md: Platform.select({
    ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6 },
    android: { elevation: 3 },
    default: { boxShadow: '0 2px 6px rgba(0,0,0,0.08)' },
  }) as any,
  lg: Platform.select({
    ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.12, shadowRadius: 12 },
    android: { elevation: 6 },
    default: { boxShadow: '0 4px 12px rgba(0,0,0,0.12)' },
  }) as any,
  colored: (color: string) => Platform.select({
    ios: { shadowColor: color, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.2, shadowRadius: 12 },
    android: { elevation: 6 },
    default: { boxShadow: `0 4px 12px ${color}33` },
  }) as any,
}

// ─── Typography ────────────────────────────────────────────────
// Follows Wise-style type scale with letter-spacing
export const typography = {
  // Display — hero numbers (savings, balance)
  displayLg: {
    fontSize: 36, fontWeight: '700' as const, fontFamily: fontFamily.bold,
    letterSpacing: -1,
  },
  display: {
    fontSize: 30, fontWeight: '700' as const, fontFamily: fontFamily.bold,
    letterSpacing: -0.8,
  },

  // Headings
  h1: {
    fontSize: 26, fontWeight: '700' as const, fontFamily: fontFamily.bold,
    letterSpacing: -0.6,
  },
  h2: {
    fontSize: 22, fontWeight: '600' as const, fontFamily: fontFamily.semiBold,
    letterSpacing: -0.4,
  },
  h3: {
    fontSize: 18, fontWeight: '600' as const, fontFamily: fontFamily.semiBold,
    letterSpacing: -0.2,
  },

  // Body
  body: {
    fontSize: 16, fontWeight: '400' as const, fontFamily: fontFamily.regular,
    letterSpacing: 0,
  },
  bodyBold: {
    fontSize: 16, fontWeight: '600' as const, fontFamily: fontFamily.semiBold,
    letterSpacing: 0,
  },

  // Captions
  caption: {
    fontSize: 14, fontWeight: '400' as const, fontFamily: fontFamily.regular,
    letterSpacing: 0.1,
  },
  captionBold: {
    fontSize: 14, fontWeight: '600' as const, fontFamily: fontFamily.semiBold,
    letterSpacing: 0.1,
  },

  // Small — minimum readable size
  small: {
    fontSize: 12, fontWeight: '400' as const, fontFamily: fontFamily.regular,
    letterSpacing: 0.2,
  },
  smallBold: {
    fontSize: 12, fontWeight: '600' as const, fontFamily: fontFamily.semiBold,
    letterSpacing: 0.2,
  },

  // Labels
  label: {
    fontSize: 13, fontWeight: '500' as const, fontFamily: fontFamily.medium,
    letterSpacing: 0.3,
  },
}

// ─── Animation Timings ─────────────────────────────────────────
export const animation = {
  fast: 150,
  normal: 250,
  slow: 350,
  spring: { damping: 15, stiffness: 150 },
}
